import {DrupalNode, DrupalTaxonomyTerm} from "next-drupal";
import {Hero} from "@/components/organisms/hero";
import {BreadCrumb, Dictionary, DrupalProductNode} from "@/types/hygiena-types";
import {buildBreadCrumbs} from "@/lib/breadcrumbs";
import dynamic from "next/dynamic";
import {useContext} from "react";
import {DictionaryContext} from "@/context/dictionary-context";
import {CONTACT_PORTAL_ID} from "@/lib/constants";

const ContentProductClass = dynamic(() => import("./content/content--product_class").then((mod) => mod.ContentProductClass));
const FrostedGlassContainer = dynamic(() => import("../../atoms/gradient-container").then((mod) => mod.FrostedGlassContainer));
const FormattedText = dynamic(() => import("../../formatted-text").then((mod) => mod.FormattedText));
const Button = dynamic(() => import("../../atoms/button").then((mod) => mod.Button));
const BreadCrumbs = dynamic(() => import("../../molecules/breadcrumbs").then((mod) => mod.BreadCrumbs));

export function TaxonomyProductClass({
  taxonomy_term,
  additionalContent,
}: {
  taxonomy_term: DrupalTaxonomyTerm;
  additionalContent: {
    children?: DrupalTaxonomyTerm[]
    products?: DrupalProductNode[],
    relatedNews?: DrupalNode[],
    relatedProducts?: DrupalProductNode[],
  };
}) {
  const t = useContext<Dictionary>(DictionaryContext);
  const breadcrumbs = [] as BreadCrumb[];
  const children = [] as DrupalTaxonomyTerm[];
  const video = taxonomy_term?.field_hero_video;
  const products = additionalContent?.products ?? [];
  const childProducts = {} as {[key: string]: DrupalProductNode[]};
  const termProducts = [] as DrupalProductNode[];

  // Map any products to the current term, if present.
  products.map(product => {
    if (product?.field_product_class?.id === taxonomy_term.id) {
      termProducts.push(product);
    }
  });

  // Get a map of products to child term.
  products.map(product => {
    if (!childProducts[product?.field_product_class?.id]) {
      childProducts[product.field_product_class.id] = [];
    }
    childProducts[product.field_product_class.id].push(product);
  });
  if (additionalContent?.children) {
    additionalContent.children.map(child => {
      if (childProducts[child?.id]) {
        children.push(child);
      }
    });
  }
  // Sort by term weight.
  children.sort((a, b) => a.weight - b.weight);

  /**
   * Scroll to the product listing section title.
   *
   * @param id
   */
  function scrollToSection(id: string) {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({behavior: 'smooth'})
    }
  }

  function jumpToContactus() {
    const form = document.getElementById(CONTACT_PORTAL_ID);
    if (form) {
      form.scrollIntoView({behavior: 'smooth'})
    }
  }

  if (taxonomy_term?.parent?.[0]?.id !== "virtual") {
    buildBreadCrumbs(taxonomy_term.parent[0], breadcrumbs);
  }
  if (taxonomy_term?.field_class_product_type_name) {
    buildBreadCrumbs(taxonomy_term.field_class_product_type_name, breadcrumbs);
  }

  return (
   <>
     <Hero image={taxonomy_term?.field_class_hero_image} video={video}>
       <div className="container mx-auto md:mx-4 lg:mx-auto flex lg:!justify-start items-center md:py-0">
         <FrostedGlassContainer className="flex flex-col rounded-none md:rounded-lg p-8 md:p-12 lg:p-16 w-full lg:!max-w-4xl">
           {breadcrumbs && <BreadCrumbs breadcrumbs={breadcrumbs}/>}
           <h1>{taxonomy_term.name}</h1>
           <div className="mb-6 font-light text-black tracking-snug leading-relaxed">
             {taxonomy_term?.field_class_hero_text?.processed && (
               <FormattedText processed={taxonomy_term.field_class_hero_text.processed}/>
             )}
           </div>
           <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
             {termProducts?.length ? (
               <Button
                 containerClassName="max-md:w-full"
                 height={20} width={20}
                 icon={"arrow-down"}
                 position={"right"}
                 key={taxonomy_term.id} callback={() => scrollToSection(taxonomy_term.id)} color={"primary"} hover={{color: "primary"}} className="w-full !py-1">{taxonomy_term.field_class_button_text}</Button>
             ) : <></>}
             {children.map(child => (
                <Button
                  containerClassName="max-md:w-full"
                  height={20} width={20}
                  icon={"arrow-down"}
                  position={"right"}
                  key={child.id} callback={() => scrollToSection(child.id)} color={"primary"} hover={{color: "primary"}} className="w-full !py-1">{child.field_class_button_text}</Button>
             ))}
           </div>
           <div className="mt-2">
             <Button callback={jumpToContactus} className="py-1.5 px-1 bg-white w-full h-full" color={"primary"} hover={{color: "primary"}} invert={true}>{`${t?.all?.request_more_info}`}</Button>
           </div>
         </FrostedGlassContainer>
       </div>
     </Hero>
     <ContentProductClass taxonomy_term={taxonomy_term} termProducts={termProducts} termChildren={children} childProducts={childProducts} additionalContent={additionalContent}/>
   </>
  )
}